// Presentation-asset continuity contract.
//
// A "presentation asset" is the imagery chosen to present a plate (the car a
// Perfect Plate is shown on, an editorial photo, or an owner's own photo). The
// point of this module is VISUAL CONTINUITY: the asset a user sees for a plate
// in Search Results must be the SAME asset they see when they open Plate Detail.
//
// It guarantees that two ways:
//   1. The asset identity is DETERMINISTIC in the plate identity — the same
//      registration resolves to the same asset on every surface, session and
//      render (no Date / Math.random).
//   2. The chosen `id` is carried through routing (see platePath callers), so
//      the destination surface pins the exact asset rather than picking its own.
//
// Plate Detail must therefore NOT independently select or regenerate a hero
// image; it resolves through here using the passed id (falling back to the
// deterministic identity mapping only when no id was supplied).

import { PlateViewerConfig } from '../models/plateViewerConfig';
import { PREMIUM_VIEWER_CONFIGS, VIEWER_CONFIGS } from '../consts/default-image';
import {
  VIEWER_CAR_META,
  bandForMake,
  normaliseMake,
} from '../consts/viewer-car-meta';
import { datedPlateYear } from './plate-year';

// Where a plate's presentation imagery originates. Carried alongside the id so
// editorial / generated / owner imagery can always be labelled accurately,
// wherever the asset is shown.
export type PresentationSource = 'editorial' | 'generated' | 'owner';

// Stable, self-describing identity of the imagery chosen to present a plate.
export interface PresentationAsset {
  /** e.g. "gen:aston-martin-db12". Stable for a given plate across surfaces. */
  id: string;
  /** editorial | generated | owner — for accurate labelling. */
  source: PresentationSource;
  /** Curated vehicle config backing a `generated` asset (null if unresolved). */
  config: PlateViewerConfig | null;
  /** Human-readable car name for alt text / labelling. */
  carName: string | null;
}

// Cars unsuitable for premium plate presentation. Preserved from the previous
// inline deny list in search-body-v2 so behaviour is unchanged.
const PREMIUM_DENY = new Set<string>([
  'Ford Explorer',
  'Fiat 500 Riva',
  'Audi RS3',
  'Audi RS5',
  'Alfa Romeo Giulia',
  'Tesla Cybertruck',
]);

// Legal dated-plate rule: a plate may not make a car look newer than it is.
// Allowed when the plate's year is no later than the car's LAST production
// year plus a small tolerance (approximate years, late first registrations),
// so the most plates possible can still use a car. Dateless / NI plates and
// vehicles without meta always pass.
export const PLATE_YEAR_TOLERANCE = 2;

export function carFitsPlate(configId: number, registration: string): boolean {
  const plateYear = datedPlateYear(registration);
  if (plateYear == null) return true;
  const meta = VIEWER_CAR_META[configId];
  if (!meta || meta.yearTo == null) return true;
  return plateYear <= meta.yearTo + PLATE_YEAR_TOLERANCE;
}

// The curated pool for this plate, filtered per call (cheap; the source array
// is small).
function premiumPool(registration: string): PlateViewerConfig[] {
  return (PREMIUM_VIEWER_CONFIGS || []).filter(
    (c) =>
      !PREMIUM_DENY.has((c.carName || '').trim()) &&
      carFitsPlate(c.id, registration)
  );
}

// Self-describing id for a generated (curated-vehicle) asset. Derived from the
// car name so it is human-readable and stable even if pool ordering changes.
export function generatedAssetId(cfg: PlateViewerConfig): string {
  const slug = (cfg.carName || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return 'gen:' + (slug || String(cfg.id));
}

// Deterministic 32-bit FNV-1a hash of the plate identity. No Date/Math.random,
// so the mapping is identical across surfaces, sessions and SSR.
function hashIdentity(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    // FNV-1a prime multiply, expressed as shifts to stay in 32-bit range.
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

function normaliseKey(reg: string): string {
  return (reg || '').toUpperCase().replace(/\s+/g, '');
}

/**
 * Resolve the presentation asset for a plate.
 *
 *  - With an explicit `assetId` (carried from the originating surface) that
 *    asset is PINNED, so the image never changes between surfaces.
 *  - Otherwise the asset is derived DETERMINISTICALLY from the plate identity,
 *    so any surface computes the same asset for the same plate.
 *  - Fallback is deterministic: if the pinned id can no longer be resolved we
 *    fall back to the identity mapping; if the pool is empty we return a null
 *    config (caller uses its own default). Fallback NEVER uses randomness.
 */
export function resolvePresentationAsset(
  registration: string,
  opts?: { assetId?: string | null; source?: PresentationSource | null }
): PresentationAsset {
  const pool = premiumPool(registration);
  const source: PresentationSource = opts?.source || 'generated';

  // 1. Pinned id from the originating surface wins.
  if (opts?.assetId) {
    const pinned = pool.find((c) => generatedAssetId(c) === opts.assetId);
    if (pinned) {
      return { id: opts.assetId, source, config: pinned, carName: pinned.carName || null };
    }
    // pinned id unresolvable -> deterministic identity fallback below.
  }

  // 2. Empty pool -> deterministic null asset (caller falls back to its default).
  if (!pool.length) {
    return { id: opts?.assetId || 'fallback:none', source, config: null, carName: null };
  }

  // 3. Deterministic mapping from the plate identity.
  const cfg = pool[hashIdentity(normaliseKey(registration)) % pool.length] || pool[0];
  return { id: generatedAssetId(cfg), source, config: cfg, carName: cfg.carName || null };
}

/**
 * Hero vehicle for Plate Detail. When the plate is on a known car now, the
 * render uses the closest vehicle we have: the same model family (a 911 shows
 * a 911), else the same make, else the same band (supercar, luxury,
 * performance, SUV, everyday). Every candidate passes the dated-plate rule and
 * the pick within a tier is deterministic in the plate, so it never changes
 * between visits. Without a current car (or no candidate) this is exactly
 * resolvePresentationAsset, which keeps continuity with Search Results.
 *
 * Order: valid pinned asset (pa) > current car tiers > deterministic default.
 */
export function resolveHeroAsset(
  registration: string,
  currentCar: {
    make?: string | null;
    model?: string | null;
    year?: number | null;
    hyper?: boolean;
  } | null,
  opts?: { assetId?: string | null; source?: PresentationSource | null }
): PresentationAsset {
  // Launch priority: a valid asset carried from the originating surface (the
  // car the visitor clicked in Search, ?pa=&pas=) is shown first, ahead of the
  // plate's association or vehicle history. An unresolvable id falls through.
  if (opts?.assetId) {
    const pinned = premiumPool(registration).find((c) => generatedAssetId(c) === opts.assetId);
    if (pinned) {
      return { id: opts.assetId, source: opts.source || 'generated', config: pinned, carName: pinned.carName || null };
    }
  }
  const make = normaliseMake(currentCar?.make);
  if (make) {
    const model = (currentCar?.model || '').toUpperCase().trim();
    const pool = (VIEWER_CONFIGS || []).filter((c) => {
      const meta = VIEWER_CAR_META[c.id];
      return !!meta && meta.hero !== false && carFitsPlate(c.id, registration);
    });
    const band = bandForMake(make);
    const tiers: PlateViewerConfig[][] = [
      pool.filter((c) => {
        const m = VIEWER_CAR_META[c.id];
        return m.make === make && !!m.family && !!model && m.family.test(model);
      }),
      pool.filter((c) => VIEWER_CAR_META[c.id].make === make),
      pool.filter((c) => {
        const m = VIEWER_CAR_META[c.id];
        return !m.sameMakeOnly && (m.type === band || (m.alsoBands || []).includes(band));
      }),
    ];
    let tier = tiers.find((t) => t.length > 0);
    // A hypercar the catalogue lacks is shown as one of its hypercars (F80 FER
    // gets the LaFerrari, not a Roma).
    if (tier && currentCar?.hyper) {
      const hyper = tier.filter((c) => VIEWER_CAR_META[c.id].hyper);
      if (hyper.length) tier = hyper;
    }
    // Within the tier, prefer cars built around the same time as the current
    // car (a 2023 Range Rover gets the 2020-on Defender, not the old one).
    const year = currentCar?.year;
    if (tier && year) {
      const sameEra = tier.filter((c) => {
        const m = VIEWER_CAR_META[c.id];
        return year >= m.yearFrom - PLATE_YEAR_TOLERANCE &&
          (m.yearTo == null || year <= m.yearTo + PLATE_YEAR_TOLERANCE);
      });
      if (sameEra.length) tier = sameEra;
    }
    if (tier) {
      const cfg = tier[hashIdentity(normaliseKey(registration)) % tier.length];
      return {
        id: generatedAssetId(cfg),
        source: 'generated',
        config: cfg,
        carName: cfg.carName || null,
      };
    }
  }
  return resolvePresentationAsset(registration, opts);
}
